import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, User, Link2, Check, BookOpen } from "lucide-react";
import api from "@/lib/api";
import { formatDate, splitParagraphs } from "@/lib/content";
import { Reveal } from "@/components/site/Motion";
import { usePageTitle } from "@/components/site/Bits";

export default function Artigo() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  usePageTitle(article?.title || "Conteúdo Jurídico");

  useEffect(() => {
    let live = true;
    setLoading(true);
    api
      .get(`/articles/${slug}`)
      .then((r) => live && setArticle(r.data))
      .catch(() => live && setArticle(null))
      .finally(() => live && setLoading(false));
  }, [slug]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = article ? `${article.title} — Silva Cotan` : "";

  const shareLinks = article
    ? [
        { label: "WhatsApp", href: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`, testid: "share-whatsapp" },
        { label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, testid: "share-linkedin" },
        { label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, testid: "share-facebook" },
      ]
    : [];

  if (loading) {
    return (
      <section className="flex min-h-[70vh] items-center justify-center bg-ivory pt-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gold border-t-transparent" aria-label="Carregando" />
      </section>
    );
  }

  if (!article) {
    return (
      <section className="flex min-h-[70vh] flex-col items-center justify-center gap-6 bg-ivory px-6 pt-20 text-center">
        <BookOpen size={36} className="text-gold-deep" strokeWidth={1.25} />
        <p className="font-display text-3xl text-navy">Artigo não encontrado</p>
        <Link to="/conteudo-juridico" data-testid="artigo-back-link" className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-gold-deep">
          <ArrowLeft size={14} /> Voltar aos conteúdos
        </Link>
      </section>
    );
  }

  return (
    <section className="bg-ivory pt-28 md:pt-36">
      <article className="mx-auto max-w-3xl px-5 pb-20 md:px-8">
        <Reveal>
          <Link to="/conteudo-juridico" data-testid="artigo-back-top-link" className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-gold-deep transition-colors hover:text-navy">
            <ArrowLeft size={13} /> Conteúdo Jurídico
          </Link>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-gold-deep" data-testid="artigo-meta">
            {article.category && <span>{article.category}</span>}
            <span className="flex items-center gap-1.5 text-inkgray/70">
              <CalendarDays size={12} /> {formatDate(article.published_at || article.created_at)}
            </span>
          </p>
          <h1 className="mt-5 font-display text-3xl leading-[1.15] text-navy md:text-5xl" data-testid="artigo-title">{article.title}</h1>
          {article.author && (
            <p className="mt-5 flex items-center gap-2 text-sm text-inkgray" data-testid="artigo-author">
              <User size={14} className="text-gold-deep" /> {article.author}
            </p>
          )}
        </Reveal>
        {article.cover_url ? (
          <Reveal delay={0.15}>
            <img src={article.cover_url} alt={article.title} className="mt-10 w-full object-cover" data-testid="artigo-cover" />
          </Reveal>
        ) : (
          <Reveal delay={0.15}>
            <img src="/media/capa-padrao.jpg" alt="" className="mt-10 w-full object-cover" data-testid="artigo-cover" />
          </Reveal>
        )}
        <div className="mt-10 space-y-6" data-testid="artigo-body">
          {splitParagraphs(article.body).map((p, i) => (
            <Reveal key={i} delay={Math.min(0.03 * i, 0.3)}>
              <p className="text-base leading-[1.85] text-inkgray md:text-lg">{p}</p>
            </Reveal>
          ))}
        </div>

        <div className="mt-14 flex flex-wrap items-center gap-3 border-t border-navy/10 pt-8">
          <span className="mr-2 text-[11px] font-bold uppercase tracking-[0.2em] text-navy">Compartilhar:</span>
          {shareLinks.map((l) => (
            <a
              key={l.label}
              href={l.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={l.testid}
              className="border border-navy/15 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-navy transition-colors hover:border-gold hover:text-gold-deep"
            >
              {l.label}
            </a>
          ))}
          <button
            data-testid="share-copy-link"
            onClick={() => {
              navigator.clipboard?.writeText(shareUrl);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            className="inline-flex items-center gap-2 border border-navy/15 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-navy transition-colors hover:border-gold hover:text-gold-deep"
          >
            {copied ? <Check size={13} className="text-gold-deep" /> : <Link2 size={13} />} {copied ? "Copiado" : "Copiar link"}
          </button>
        </div>
      </article>
    </section>
  );
}
